import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  EiaCase,
  EiaCaseState,
  EiaComment,
  EiaDocument,
  EiaHearing,
  EiaMonitoringRequirement,
  EiaPanelReview,
  MonitoringFrequency,
  Page,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import {
  Detail,
  EmptyState,
  ErrorNote,
  Field,
  FileHashField,
  Modal,
  PageHeader,
  RoleNotice,
  Spinner,
  StatusBadge,
  TabStrip,
  type FileHashValue,
} from '../../ui/components';

const OFFICER_ROLES = ['ENV_OFFICER'];
const STATES: EiaCaseState[] = ['SCREENING', 'SCOPING', 'EIS_REVIEW', 'CONSULTATION', 'TECHNICAL_REVIEW', 'DECIDED', 'WITHDRAWN'];
const MONITORING_FREQUENCIES: MonitoringFrequency[] = ['MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL'];

/**
 * EIA case lifecycle: SCREENING -> SCOPING -> EIS_REVIEW -> CONSULTATION ->
 * TECHNICAL_REVIEW -> DECIDED (+ post-decision monitoring plan), or
 * WITHDRAWN at any point before DECIDED (M31).
 *
 * GET /eia-cases (the list) is ENV_OFFICER-only — EIA_PANEL members (who
 * only need to record their own technical review) have no list endpoint of
 * their own and can't even list a case's other panel reviews (also
 * ENV_OFFICER-gated); see the "Open a case by ID" fallback below, a direct,
 * honest reflection of that real M31 constraint rather than a workaround.
 */
export function EiaCases() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENV_OFFICER') ?? false;
  const isPanel = persona?.roles.includes('EIA_PANEL') ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openIdInput, setOpenIdInput] = useState('');

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m31/eia-cases?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<EiaCase>>(isOfficer ? path : null);

  return (
    <div>
      <PageHeader
        title="EIA Cases"
        subtitle="Screening -> scoping -> EIS review -> public consultation -> technical review -> decision -> post-decision monitoring (M31)."
        actions={
          isOfficer ? (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-52" placeholder="Ref no or description…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-primary">
                Search
              </button>
            </form>
          ) : undefined
        }
      />

      {!isOfficer ? (
        <div className="mb-4 space-y-3">
          <RoleNotice roles={OFFICER_ROLES} />
          {isPanel ? (
            <div className="card p-4">
              <div className="mb-1 text-sm font-semibold text-slate-700">Open a case by ID</div>
              <p className="mb-2 text-xs text-slate-500">
                EIA_PANEL members cannot list cases (GET /eia-cases requires ENV_OFFICER) — open the specific case an officer has referred to you
                for technical review.
              </p>
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (openIdInput.trim()) setSelectedId(openIdInput.trim());
                }}
              >
                <input className="field-input" placeholder="EiaCase id" value={openIdInput} onChange={(e) => setOpenIdInput(e.target.value)} />
                <button className="btn-primary" type="submit">
                  Open
                </button>
              </form>
            </div>
          ) : null}
        </div>
      ) : null}

      {isOfficer ? (
        <>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No EIA cases match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Source</th>
                    <th className="table-head">Sector</th>
                    <th className="table-head">Class</th>
                    <th className="table-head">Fee</th>
                    <th className="table-head">State</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((c) => (
                    <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{c.refNo}</td>
                      <td className="table-cell text-xs text-slate-500">
                        {c.sourceModule}
                        {c.projectRef ? ` · ${c.projectRef}` : ''}
                      </td>
                      <td className="table-cell text-xs text-slate-500">{c.projectSector}</td>
                      <td className="table-cell">{c.class ?? c.suggestedClass ? <StatusBadge status={c.class ?? c.suggestedClass!} /> : '—'}</td>
                      <td className="table-cell text-xs">
                        {scr(c.feeAmountScr)} · <StatusBadge status={c.feeStatus} />
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={c.state} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? (
        <CaseDetailModal
          id={selectedId}
          isOfficer={isOfficer}
          isPanel={isPanel}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

type Tab = 'overview' | 'documents' | 'consultation' | 'panel' | 'monitoring';

function CaseDetailModal({
  id,
  isOfficer,
  isPanel,
  onClose,
  onChanged,
}: {
  id: string;
  isOfficer: boolean;
  isPanel: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const caseQ = useApiQuery<EiaCase>(`/api/v1/m31/eia-cases/${id}`);
  const docsQ = useApiQuery<{ items: EiaDocument[] }>(`/api/v1/m31/eia-cases/${id}/documents`);
  const commentsQ = useApiQuery<{ items: EiaComment[] }>(`/api/v1/m31/eia-cases/${id}/comments`);
  const hearingsQ = useApiQuery<{ items: EiaHearing[] }>(`/api/v1/m31/eia-cases/${id}/hearings`);
  const panelQ = useApiQuery<{ items: EiaPanelReview[] }>(isOfficer ? `/api/v1/m31/eia-cases/${id}/panel-reviews` : null);
  const monitoringQ = useApiQuery<{ items: EiaMonitoringRequirement[] }>(`/api/v1/m31/eia-cases/${id}/monitoring-requirements`);

  const [tab, setTab] = useState<Tab>('overview');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const c = caseQ.data;
  const refreshAll = () => {
    caseQ.reload();
    docsQ.reload();
    commentsQ.reload();
    hearingsQ.reload();
    panelQ.reload();
    monitoringQ.reload();
    onChanged();
  };

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refreshAll();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={c ? `${c.refNo} — ${c.projectSector}` : 'EIA case'} onClose={onClose} wide>
      {caseQ.error ? <ErrorNote message={caseQ.error} /> : null}
      {caseQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {c ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <Detail label="State" value={<StatusBadge status={c.state} />} />
            <Detail label="Class" value={c.class ?? c.suggestedClass ? <StatusBadge status={(c.class ?? c.suggestedClass)!} /> : '—'} />
            <Detail label="Fee" value={`${scr(c.feeAmountScr)} (${c.feeStatus})`} />
            <Detail label="Protected area nearby" value={c.protectedAreaNearby ? 'Yes' : 'No'} />
            <Detail label="Parcel" value={c.parcelNo} mono />
            <Detail label="Applicant" value={`${c.applicantId} (${c.applicantType})`} mono />
            <Detail label="Source" value={`${c.sourceModule}${c.projectRef ? ` · ${c.projectRef}` : ''}`} />
            <Detail label="Created" value={formatDateTime(c.createdAt)} />
          </div>
          <p className="text-sm text-slate-700">{c.projectDescription}</p>

          <TabStrip
            tabs={[
              { key: 'overview', label: 'Overview & decision' },
              { key: 'documents', label: `Documents (${docsQ.data?.items.length ?? 0})` },
              { key: 'consultation', label: `Consultation (${commentsQ.data?.items.length ?? 0}c/${hearingsQ.data?.items.length ?? 0}h)` },
              { key: 'panel', label: 'Technical panel' },
              { key: 'monitoring', label: `Monitoring (${monitoringQ.data?.items.length ?? 0})` },
            ]}
            active={tab}
            onChange={setTab}
          />

          {tab === 'overview' ? (
            <OverviewTab c={c} isOfficer={isOfficer} busy={busy} act={act} docsCount={docsQ.data?.items.length ?? 0} />
          ) : null}
          {tab === 'documents' ? (
            <DocumentsTab id={id} c={c} isOfficer={isOfficer} docs={docsQ.data?.items ?? []} busy={busy} act={act} error={docsQ.error} />
          ) : null}
          {tab === 'consultation' ? (
            <ConsultationTab
              id={id}
              c={c}
              isOfficer={isOfficer}
              comments={commentsQ.data?.items ?? []}
              hearings={hearingsQ.data?.items ?? []}
              busy={busy}
              act={act}
            />
          ) : null}
          {tab === 'panel' ? (
            <PanelTab id={id} c={c} isOfficer={isOfficer} isPanel={isPanel} reviews={panelQ.data?.items ?? null} busy={busy} act={act} />
          ) : null}
          {tab === 'monitoring' ? (
            <MonitoringTab id={id} isOfficer={isOfficer} requirements={monitoringQ.data?.items ?? []} busy={busy} act={act} />
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function OverviewTab({
  c,
  isOfficer,
  busy,
  act,
  docsCount,
}: {
  c: EiaCase;
  isOfficer: boolean;
  busy: boolean;
  act: (fn: () => Promise<unknown>) => void;
  docsCount: number;
}) {
  const [overrideClass, setOverrideClass] = useState<'' | 'ONE' | 'TWO' | 'THREE'>('');
  const [overrideReason, setOverrideReason] = useState('');
  const [outcome, setOutcome] = useState<'APPROVED' | 'APPROVED_WITH_CONDITIONS' | 'REFUSED'>('APPROVED');
  const [conditions, setConditions] = useState('');
  const [reasons, setReasons] = useState('');
  const [monitoringParam, setMonitoringParam] = useState('');
  const [monitoringFreq, setMonitoringFreq] = useState<MonitoringFrequency>('QUARTERLY');
  const [monitoringPlan, setMonitoringPlan] = useState<Array<{ parameter: string; frequency: MonitoringFrequency }>>([]);
  const [withdrawReason, setWithdrawReason] = useState('');

  const canWithdraw = c.state !== 'DECIDED' && c.state !== 'WITHDRAWN';
  const canRespondReferral = c.sourceModule === 'M17' && c.state === 'DECIDED' && !c.referralRespondedAt;

  return (
    <div className="space-y-4">
      {c.state === 'SCREENING' && isOfficer ? (
        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
          <div className="text-sm font-semibold text-slate-700">Confirm screening class</div>
          <p className="text-xs text-slate-500">
            Suggested class: <StatusBadge status={c.suggestedClass ?? '—'} /> — leave blank to accept it, or override with a reason. Class THREE
            auto-decides (categorical exclusion — approved immediately).
          </p>
          <div className="flex gap-2">
            <select className="field-input !w-auto" value={overrideClass} onChange={(e) => setOverrideClass(e.target.value as never)}>
              <option value="">Accept suggested class</option>
              <option value="ONE">Override: ONE</option>
              <option value="TWO">Override: TWO</option>
              <option value="THREE">Override: THREE</option>
            </select>
          </div>
          {overrideClass ? (
            <input className="field-input" placeholder="Reason for override" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
          ) : null}
          <button
            className="btn-primary"
            disabled={busy || c.feeStatus !== 'PAID' || (overrideClass !== '' && overrideReason.trim().length === 0)}
            onClick={() =>
              act(() =>
                api.post(`/api/v1/m31/eia-cases/${c.id}/screen`, {
                  ...(overrideClass ? { overrideClass, overrideReason } : {}),
                }),
              )
            }
          >
            Confirm screening
          </button>
          {c.feeStatus !== 'PAID' ? <p className="text-xs text-rose-600">Screening fee not yet confirmed paid (SeyPay webhook, service-token only).</p> : null}
        </div>
      ) : null}

      {c.state === 'SCOPING' && isOfficer ? (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-1 text-sm font-semibold text-slate-700">Advance to EIS review</div>
          <p className="mb-2 text-xs text-slate-500">Requires at least one SCOPING document uploaded (see Documents tab) — {docsCount} document(s) on file.</p>
          <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/eia-cases/${c.id}/advance-to-eis`))}>
            Advance to EIS review
          </button>
        </div>
      ) : null}

      {c.state === 'EIS_REVIEW' && isOfficer ? (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-1 text-sm font-semibold text-slate-700">Open public consultation</div>
          <p className="mb-2 text-xs text-slate-500">Requires at least one EIS document uploaded — publishes an OpenGov (C11) notice and opens a 21-day comment window.</p>
          <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/eia-cases/${c.id}/open-consultation`))}>
            Open consultation
          </button>
        </div>
      ) : null}

      {c.state === 'CONSULTATION' && isOfficer ? (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-1 text-sm font-semibold text-slate-700">Close consultation</div>
          <p className="mb-2 text-xs text-slate-500">
            Window closes {formatDateTime(c.consultationClosesAt)} — cannot close before then. Advances to TECHNICAL_REVIEW.
          </p>
          <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/eia-cases/${c.id}/close-consultation`))}>
            Close consultation
          </button>
        </div>
      ) : null}

      {c.state === 'TECHNICAL_REVIEW' && isOfficer ? (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-2 text-sm font-semibold text-slate-700">Record decision</div>
          <p className="mb-2 text-xs text-slate-500">Requires at least one EIA_PANEL technical review on file (see Technical panel tab).</p>
          <div className="grid gap-2 sm:grid-cols-3">
            <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as never)}>
              <option value="APPROVED">Approved</option>
              <option value="APPROVED_WITH_CONDITIONS">Approved with conditions</option>
              <option value="REFUSED">Refused</option>
            </select>
            {outcome !== 'REFUSED' ? (
              <input className="field-input sm:col-span-2" placeholder="Conditions (comma separated, optional)" value={conditions} onChange={(e) => setConditions(e.target.value)} />
            ) : (
              <input className="field-input sm:col-span-2" placeholder="Reasons for refusal (comma separated, required)" value={reasons} onChange={(e) => setReasons(e.target.value)} />
            )}
          </div>
          {outcome !== 'REFUSED' ? (
            <div className="mt-2 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Monitoring plan (optional)</div>
              <div className="flex gap-2">
                <input className="field-input" placeholder="Parameter, e.g. Turbidity at outfall" value={monitoringParam} onChange={(e) => setMonitoringParam(e.target.value)} />
                <select className="field-input !w-auto" value={monitoringFreq} onChange={(e) => setMonitoringFreq(e.target.value as MonitoringFrequency)}>
                  {MONITORING_FREQUENCIES.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                <button
                  className="btn-secondary"
                  type="button"
                  disabled={!monitoringParam.trim()}
                  onClick={() => {
                    setMonitoringPlan((p) => [...p, { parameter: monitoringParam.trim(), frequency: monitoringFreq }]);
                    setMonitoringParam('');
                  }}
                >
                  + Add
                </button>
              </div>
              {monitoringPlan.length > 0 ? (
                <ul className="space-y-1 text-xs text-slate-600">
                  {monitoringPlan.map((m, i) => (
                    <li key={i} className="flex items-center justify-between rounded border border-slate-100 px-2 py-1">
                      {m.parameter} — {m.frequency}
                      <button className="text-rose-500" onClick={() => setMonitoringPlan((p) => p.filter((_, idx) => idx !== i))}>
                        remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <button
            className="btn-primary mt-3"
            disabled={busy || (outcome === 'REFUSED' && reasons.trim().length === 0)}
            onClick={() =>
              act(() =>
                api.post(`/api/v1/m31/eia-cases/${c.id}/decide`, {
                  outcome,
                  conditions: outcome === 'APPROVED_WITH_CONDITIONS' ? conditions.split(',').map((s) => s.trim()).filter(Boolean) : [],
                  reasons: outcome === 'REFUSED' ? reasons.split(',').map((s) => s.trim()).filter(Boolean) : [],
                  monitoringPlan,
                }),
              )
            }
          >
            Record decision
          </button>
        </div>
      ) : null}

      {c.state === 'DECIDED' ? (
        <div className="rounded-lg border border-slate-200 p-3 text-sm">
          <div className="mb-1 font-semibold text-slate-700">Decision</div>
          <StatusBadge status={c.decisionOutcome ?? '—'} /> · decided {formatDateTime(c.decidedAt)} by {c.decidedBy}
          {c.eiaCertSerial ? (
            <div className="mt-1 text-xs text-slate-500">
              Clearance certificate <span className="font-mono">{c.eiaCertSerial}</span>
            </div>
          ) : null}
          {c.refusalReasons && c.refusalReasons.length > 0 ? <p className="mt-1 text-xs text-rose-600">Reasons: {c.refusalReasons.join('; ')}</p> : null}
          {c.approvalConditions && c.approvalConditions.length > 0 ? <p className="mt-1 text-xs text-slate-600">Conditions: {c.approvalConditions.join('; ')}</p> : null}
          {canRespondReferral && isOfficer ? (
            <button className="btn-secondary mt-2" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/eia-cases/${c.id}/respond-referral`))}>
              Respond to M17 referral now
            </button>
          ) : c.sourceModule === 'M17' && c.referralRespondedAt ? (
            <p className="mt-1 text-xs text-seagreen-600">M17 referral responded {formatDateTime(c.referralRespondedAt)}.</p>
          ) : null}
        </div>
      ) : null}

      {canWithdraw ? (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-1 text-sm font-semibold text-slate-700">Withdraw case</div>
          <div className="flex gap-2">
            <input className="field-input" placeholder="Reason (optional)" value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
            <button className="btn-danger" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/eia-cases/${c.id}/withdraw`, { reason: withdrawReason || undefined }))}>
              Withdraw
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DocumentsTab({
  id,
  c,
  isOfficer,
  docs,
  busy,
  act,
  error,
}: {
  id: string;
  c: EiaCase;
  isOfficer: boolean;
  docs: EiaDocument[];
  busy: boolean;
  act: (fn: () => Promise<unknown>) => void;
  error: string | null;
}) {
  const [file, setFile] = useState<FileHashValue | null>(null);
  const uploadType = c.state === 'SCOPING' ? 'scoping-doc' : c.state === 'EIS_REVIEW' ? 'eis-documents' : null;

  return (
    <div className="space-y-3">
      {error ? <ErrorNote message={error} /> : null}
      {uploadType ? (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-2 text-sm font-semibold text-slate-700">
            Upload {uploadType === 'scoping-doc' ? 'scoping document' : 'EIS document'}
          </div>
          <FileHashField label="Document" onChange={setFile} required />
          <button
            className="btn-primary mt-2"
            disabled={busy || !file}
            onClick={() =>
              act(async () => {
                if (!file) return;
                if (uploadType === 'scoping-doc') {
                  await api.post(`/api/v1/m31/eia-cases/${id}/scoping-doc`, { docRef: file.docRef, sha256: file.sha256, fileName: file.fileName });
                } else {
                  await api.post(`/api/v1/m31/eia-cases/${id}/eis-documents`, { docType: 'EIS', docRef: file.docRef, sha256: file.sha256, fileName: file.fileName });
                }
                setFile(null);
              })
            }
          >
            Upload
          </button>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Documents can only be added while the case is in SCOPING (scoping doc) or EIS_REVIEW (EIS docs).</p>
      )}
      {docs.length === 0 ? (
        <EmptyState title="No documents on file" />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="table-head">Type</th>
              <th className="table-head">File</th>
              <th className="table-head">SHA-256</th>
              <th className="table-head">Uploaded</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id}>
                <td className="table-cell">
                  <StatusBadge status={d.docType} />
                </td>
                <td className="table-cell">{d.fileName ?? d.docRef}</td>
                <td className="table-cell font-mono text-[11px] text-slate-400">{d.sha256.slice(0, 16)}…</td>
                <td className="table-cell text-xs text-slate-500">{formatDateTime(d.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ConsultationTab({
  id,
  c,
  isOfficer,
  comments,
  hearings,
  busy,
  act,
}: {
  id: string;
  c: EiaCase;
  isOfficer: boolean;
  comments: EiaComment[];
  hearings: EiaHearing[];
  busy: boolean;
  act: (fn: () => Promise<unknown>) => void;
}) {
  const [commenterName, setCommenterName] = useState('');
  const [commentText, setCommentText] = useState('');
  const [venue, setVenue] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const open = c.state === 'CONSULTATION';

  return (
    <div className="space-y-4">
      {open ? (
        <p className="text-xs text-slate-500">
          Public comment window: {formatDateTime(c.consultationOpensAt)} → {formatDateTime(c.consultationClosesAt)}
          {c.publicNoticeRef ? ` (OpenGov notice ${c.publicNoticeRef})` : ''}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-2 text-sm font-semibold text-slate-700">Public comments ({comments.length})</div>
          {open ? (
            <div className="mb-2 space-y-1.5 rounded-lg border border-slate-200 p-2.5">
              <input className="field-input" placeholder="Commenter name" value={commenterName} onChange={(e) => setCommenterName(e.target.value)} />
              <textarea className="field-input" rows={2} placeholder="Comment" value={commentText} onChange={(e) => setCommentText(e.target.value)} />
              <button
                className="btn-secondary"
                disabled={busy || commenterName.trim().length < 2 || commentText.trim().length < 3}
                onClick={() =>
                  act(async () => {
                    await api.post(`/api/v1/m31/eia-cases/${id}/comments`, { commenterName, comment: commentText });
                    setCommenterName('');
                    setCommentText('');
                  })
                }
              >
                Record comment
              </button>
            </div>
          ) : null}
          <div className="max-h-56 space-y-1.5 overflow-y-auto">
            {comments.length === 0 ? (
              <p className="text-xs text-slate-400">No comments recorded.</p>
            ) : (
              comments.map((cm) => (
                <div key={cm.id} className="rounded-lg border border-slate-100 px-2.5 py-1.5 text-xs">
                  <div className="font-semibold text-slate-700">{cm.commenterName}</div>
                  <div className="text-slate-600">{cm.comment}</div>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <div className="mb-2 text-sm font-semibold text-slate-700">Hearings ({hearings.length})</div>
          {open && isOfficer ? (
            <div className="mb-2 space-y-1.5 rounded-lg border border-slate-200 p-2.5">
              <input className="field-input" placeholder="Venue" value={venue} onChange={(e) => setVenue(e.target.value)} />
              <input className="field-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
              <button
                className="btn-secondary"
                disabled={busy || venue.trim().length < 2 || !scheduledAt}
                onClick={() =>
                  act(async () => {
                    await api.post(`/api/v1/m31/eia-cases/${id}/hearings`, { venue, scheduledAt: new Date(scheduledAt).toISOString() });
                    setVenue('');
                    setScheduledAt('');
                  })
                }
              >
                Schedule hearing
              </button>
            </div>
          ) : null}
          <div className="max-h-56 space-y-1.5 overflow-y-auto">
            {hearings.length === 0 ? (
              <p className="text-xs text-slate-400">No hearings scheduled.</p>
            ) : (
              hearings.map((h) => (
                <div key={h.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-2.5 py-1.5 text-xs">
                  <span>
                    {h.venue} — {formatDateTime(h.scheduledAt)}
                  </span>
                  <StatusBadge status={h.status} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PanelTab({
  id,
  c,
  isOfficer,
  isPanel,
  reviews,
  busy,
  act,
}: {
  id: string;
  c: EiaCase;
  isOfficer: boolean;
  isPanel: boolean;
  reviews: EiaPanelReview[] | null;
  busy: boolean;
  act: (fn: () => Promise<unknown>) => void;
}) {
  const [recommendation, setRecommendation] = useState<'APPROVE' | 'APPROVE_WITH_CONDITIONS' | 'REFUSE' | 'NEEDS_REVISION'>('APPROVE');
  const [comments, setComments] = useState('');
  const canReview = isPanel && c.state === 'TECHNICAL_REVIEW';

  return (
    <div className="space-y-4">
      {canReview ? (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="mb-2 text-sm font-semibold text-slate-700">Record your technical review</div>
          <div className="grid gap-2 sm:grid-cols-2">
            <select className="field-input" value={recommendation} onChange={(e) => setRecommendation(e.target.value as never)}>
              <option value="APPROVE">Approve</option>
              <option value="APPROVE_WITH_CONDITIONS">Approve with conditions</option>
              <option value="REFUSE">Refuse</option>
              <option value="NEEDS_REVISION">Needs revision</option>
            </select>
            <input className="field-input" placeholder="Comments (optional)" value={comments} onChange={(e) => setComments(e.target.value)} />
          </div>
          <button
            className="btn-primary mt-2"
            disabled={busy}
            onClick={() =>
              act(async () => {
                await api.post(`/api/v1/m31/eia-cases/${id}/panel-reviews`, { recommendation, comments: comments || undefined, conditions: [] });
                setComments('');
              })
            }
          >
            Submit review
          </button>
        </div>
      ) : null}

      {!isOfficer ? (
        <p className="text-xs text-slate-400">
          Listing every panel member's review requires ENV_OFFICER (GET /eia-cases/:id/panel-reviews is officer-only) — a panel member only sees
          the confirmation of their own submission above.
        </p>
      ) : reviews === null ? null : reviews.length === 0 ? (
        <EmptyState title="No technical reviews recorded yet" />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="table-head">Reviewer</th>
              <th className="table-head">Recommendation</th>
              <th className="table-head">Comments</th>
              <th className="table-head">Recorded</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((r) => (
              <tr key={r.id}>
                <td className="table-cell font-mono text-xs">{r.reviewerId}</td>
                <td className="table-cell">
                  <StatusBadge status={r.recommendation} />
                </td>
                <td className="table-cell text-xs text-slate-500">{r.comments ?? '—'}</td>
                <td className="table-cell text-xs text-slate-500">{formatDateTime(r.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function MonitoringTab({
  id,
  isOfficer,
  requirements,
  busy,
  act,
}: {
  id: string;
  isOfficer: boolean;
  requirements: EiaMonitoringRequirement[];
  busy: boolean;
  act: (fn: () => Promise<unknown>) => void;
}) {
  const [fileByReq, setFileByReq] = useState<Record<string, FileHashValue | null>>({});
  const [notesByReq, setNotesByReq] = useState<Record<string, string>>({});
  const [satisfactoryByReq, setSatisfactoryByReq] = useState<Record<string, boolean>>({});

  if (requirements.length === 0) return <EmptyState title="No monitoring plan on this case" hint="A monitoring plan is created when the case is approved with one attached at decision time." />;

  return (
    <div className="space-y-3">
      {requirements.map((r) => (
        <div key={r.id} className="rounded-lg border border-slate-200 p-3">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-700">
              {r.parameter} <span className="text-xs font-normal text-slate-400">({r.frequency})</span>
            </div>
            <StatusBadge status={r.status} />
          </div>
          <div className="text-xs text-slate-500">Next due {formatDate(r.nextDueAt)}</div>

          {r.status !== 'WAIVED' ? (
            <div className="mt-2 space-y-1.5">
              <FileHashField label="Submit monitoring report" onChange={(v) => setFileByReq((s) => ({ ...s, [r.id]: v }))} />
              <button
                className="btn-secondary"
                disabled={busy || !fileByReq[r.id]}
                onClick={() =>
                  act(async () => {
                    const f = fileByReq[r.id]!;
                    await api.post(`/api/v1/m31/eia-cases/${id}/monitoring-requirements/${r.id}/submissions`, { docRef: f.docRef, sha256: f.sha256 });
                    setFileByReq((s) => ({ ...s, [r.id]: null }));
                  })
                }
              >
                Submit report
              </button>
            </div>
          ) : null}

          {r.submissions && r.submissions.length > 0 ? (
            <div className="mt-2 space-y-1.5">
              {r.submissions.map((s) => (
                <div key={s.id} className="rounded border border-slate-100 px-2 py-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span>Submitted {formatDateTime(s.submittedAt)}</span>
                    {s.reviewedAt ? <span className="text-seagreen-600">reviewed {formatDateTime(s.reviewedAt)}</span> : <span className="text-amber-600">awaiting review</span>}
                  </div>
                  {isOfficer && !s.reviewedAt ? (
                    <div className="mt-1.5 flex items-center gap-2">
                      <input
                        className="field-input !w-auto flex-1 !py-1"
                        placeholder="Review notes (optional)"
                        value={notesByReq[s.id] ?? ''}
                        onChange={(e) => setNotesByReq((n) => ({ ...n, [s.id]: e.target.value }))}
                      />
                      <label className="flex items-center gap-1 text-[11px]">
                        <input
                          type="checkbox"
                          checked={satisfactoryByReq[s.id] ?? true}
                          onChange={(e) => setSatisfactoryByReq((sf) => ({ ...sf, [s.id]: e.target.checked }))}
                        />
                        Satisfactory
                      </label>
                      <button
                        className="btn-secondary !px-2 !py-1 text-[11px]"
                        disabled={busy}
                        onClick={() =>
                          act(() =>
                            api.post(`/api/v1/m31/eia-cases/${id}/monitoring-requirements/${r.id}/submissions/${s.id}/review`, {
                              satisfactory: satisfactoryByReq[s.id] ?? true,
                              notes: notesByReq[s.id] || undefined,
                            }),
                          )
                        }
                      >
                        Review
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
